import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../contexts/AuthContext';
import { LineGraph, Scorecard } from '../components/utility/Charts';
import { StoreDateRangeFilter, Caret, TablePagination } from '../components/utility/Utility';
import AnalyticsSourceSelector from '../components/AnalyticsSourceSelector';
import { IdNameMultiSelectFilter, MultiSelectFilter, SingleSelectDropdown } from './ProductAnalysis';
import { storeApiBoundsFromMatch, storeMatchRangeLastNCalendarDays } from '../utils/storeDateRange';
import {
  CUSTOM_RANGE_LABEL,
  DEFAULT_RANGE_LABEL,
  RANGE_PRESETS,
  parseViewState,
  resolveRangeFromUrl,
  serializeRangeState
} from '../utils/productAnalysisUrlState';
import '../assets/css/components/program.css';
import '../assets/css/charts.css';
import loadingBuffering from '../assets/images/loading-buffering.gif';

axios.defaults.withCredentials = true;

const API = 'https://api.amplifyloyalty.com/app/meta-ads';
const TABLE_PAGE_SIZE = 50;
const CHILD_PAGE_SIZE = 100;

const TIME_GRAIN_OPTIONS = [
  { id: 'month', label: 'Month' },
  { id: 'day', label: 'Day' },
  { id: 'weekday', label: 'Day of Week' }
];

const SERIES_METRICS = {
  Spend: { id: 'Spend', label: 'Spend', format: 'money' },
  Impressions: { id: 'Impressions', label: 'Impressions', format: 'count' },
  Clicks: { id: 'Clicks', label: 'Clicks', format: 'count' },
  CTR: { id: 'CTR', label: 'CTR', format: 'percent' },
  CPC: { id: 'CPC', label: 'CPC', format: 'money' },
  CPM: { id: 'CPM', label: 'CPM', format: 'money' },
  Purchases: { id: 'Purchases', label: 'Purchases', format: 'decimal' },
  PurchaseValue: { id: 'PurchaseValue', label: 'Purchase Value', format: 'money' },
  ROAS: { id: 'ROAS', label: 'ROAS', format: 'ratio' },
  CPA: { id: 'CPA', label: 'CPA', format: 'money' }
};
const SERIES_NONE = { id: null, label: 'None' };
const SERIES_METRIC_OPTIONS = Object.values(SERIES_METRICS);

const TABLE_COLUMNS = [
  { id: 'spend', label: 'Spend', format: 'money' },
  { id: 'impressions', label: 'Impr.', format: 'count' },
  { id: 'clicks', label: 'Clicks', format: 'count' },
  { id: 'ctr', label: 'CTR', format: 'percent' },
  { id: 'cpc', label: 'CPC', format: 'money' },
  { id: 'cpm', label: 'CPM', format: 'money' },
  { id: 'purchases', label: 'Purch.', format: 'decimal' },
  { id: 'purchase_value', label: 'Purch. Value', format: 'money' },
  { id: 'roas', label: 'ROAS', format: 'ratio' },
  { id: 'cpa', label: 'CPA', format: 'money' }
];

const CHANGE_LEVEL_OPTIONS = [
  { id: 'all', label: 'All levels' },
  { id: 'campaign', label: 'Campaign' },
  { id: 'adset', label: 'Ad set' },
  { id: 'ad', label: 'Ad' },
  { id: 'account', label: 'Account' }
];

const CHANGE_TYPE_LABELS = {
  created: 'Launched',
  status_change: 'Status',
  budget_change: 'Budget',
  bid_change: 'Bid strategy',
  creative_change: 'Creative',
  targeting_change: 'Targeting',
  schedule_change: 'Schedule',
  other: 'Other'
};

const CHANGE_TYPE_COLORS = {
  created: '#2e9e5b',
  status_change: '#6b6b6b',
  budget_change: '#F8A305',
  bid_change: '#8a5cf6',
  creative_change: '#1877F2',
  targeting_change: '#c2410c',
  schedule_change: '#0f766e',
  other: '#9a9a9a'
};

const LEVEL_LABELS = { account: 'Account', campaign: 'Campaign', adset: 'Ad set', ad: 'Ad' };
const WEEKDAY_ORDER = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const EMPTY_FILTERS = {
  campaign_ids: [],
  adset_ids: [],
  ad_ids: [],
  effective_statuses: [],
  objectives: []
};

function humanize(s) {
  return String(s || '')
    .toLowerCase()
    .replace(/^outcome_/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatGrainTick(value, grain) {
  const s = String(value ?? '');
  if (grain === 'month') {
    const [y, m] = s.split('-');
    return `${MONTH_LABELS[parseInt(m, 10) - 1] || m} ${String(y).slice(-2)}`;
  }
  if (grain === 'weekday') return s;
  const p = s.split('-');
  return p.length >= 3 ? `${parseInt(p[1], 10)}/${parseInt(p[2], 10)}` : s;
}

function formatGrainLabel(value, grain) {
  const s = String(value ?? '');
  if (grain === 'month') {
    const [y, m] = s.split('-');
    return `${MONTH_LABELS[parseInt(m, 10) - 1] || m} ${y}`;
  }
  if (grain === 'weekday') return s;
  const p = s.split('-');
  return p.length >= 3 ? `${parseInt(p[1], 10)}/${parseInt(p[2], 10)}/${p[0]}` : s;
}

/** Narrowed ids only: a full selection means "no filter" and is omitted from the request. */
function narrowed(selected, universe) {
  if (!universe.length || selected.length >= universe.length) return undefined;
  return selected;
}

export default function MetaAdsAnalytics() {
  const { getJwtToken, userProfile } = useContext(AuthContext);
  const timezone = userProfile?.store?.settings?.timezone;

  const [searchParams, setSearchParams] = useSearchParams();
  const urlStateRef = useRef(null);
  if (urlStateRef.current === null) urlStateRef.current = parseViewState(searchParams);

  const [activeRange, setActiveRange] = useState(DEFAULT_RANGE_LABEL);
  const [match, setMatch] = useState(() => {
    const start = new Date();
    start.setDate(start.getDate() - 7);
    const end = new Date();
    end.setDate(end.getDate() - 1);
    return { start, end };
  });
  const seededMatchForTz = useRef(null);
  const [matchReadyTz, setMatchReadyTz] = useState(null);

  const [connected, setConnected] = useState(null);
  const [candidates, setCandidates] = useState(null);
  const [candidatesRangeKey, setCandidatesRangeKey] = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTERS);

  const [timeGrain, setTimeGrain] = useState('day');
  const [leftMetric, setLeftMetric] = useState('Spend');
  const [rightMetric, setRightMetric] = useState('ROAS');
  const [showLeftPrior, setShowLeftPrior] = useState(false);
  const [showRightPrior, setShowRightPrior] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [error, setError] = useState('');

  const [tableSort, setTableSort] = useState({ sort_by: 'spend', sort_dir: -1 });
  const [tablePage, setTablePage] = useState(1);
  const [table, setTable] = useState(null);
  const [loadingTable, setLoadingTable] = useState(false);
  // { [`campaign:${id}` | `adset:${id}`]: { status, rows } }, valid for `childrenKeyRef`.
  const [children, setChildren] = useState({});
  const [expanded, setExpanded] = useState({});
  const childrenKeyRef = useRef(null);

  const [changeLevel, setChangeLevel] = useState('all');

  const currency = analytics?.currency || table?.currency || 'USD';
  const numberFormat = useMemo(() => new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }), []);
  const decimalFormat = useMemo(() => new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }), []);
  const moneyFormat = useMemo(
    () => new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 2 }),
    [currency]
  );
  const moneyTick = useMemo(
    () => new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }),
    [currency]
  );

  const fmt = (kind, v) => {
    const n = Number(v) || 0;
    if (kind === 'money') return moneyFormat.format(n);
    if (kind === 'percent') return `${Math.round(n * 10000) / 100}%`;
    if (kind === 'ratio') return decimalFormat.format(n);
    if (kind === 'decimal') return decimalFormat.format(n);
    return numberFormat.format(n);
  };
  const fmtTick = (kind, v) => {
    const n = Number(v) || 0;
    if (kind === 'money') return moneyTick.format(n);
    if (kind === 'percent') return `${Math.round(n * 1000) / 10}%`;
    if (kind === 'ratio') return decimalFormat.format(n);
    return numberFormat.format(n);
  };

  // Same URL-range seeding as the Shopify page so switching sources keeps the window.
  useEffect(() => {
    if (!timezone || seededMatchForTz.current === timezone) return;
    seededMatchForTz.current = timezone;
    const fromUrl = resolveRangeFromUrl(timezone, urlStateRef.current.range);
    if (fromUrl) {
      setMatch(fromUrl.match);
      setActiveRange(fromUrl.activeRange);
    } else {
      setMatch(storeMatchRangeLastNCalendarDays(timezone, 7));
      setActiveRange(DEFAULT_RANGE_LABEL);
    }
    setMatchReadyTz(timezone);
  }, [timezone]);

  useEffect(() => {
    if (!timezone || matchReadyTz !== timezone) return;
    const next = serializeRangeState({ activeRange, match, timezone });
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRange, match, timezone, matchReadyTz]);

  // Filter options follow the selected range: only campaigns/ad sets/ads with spend > 0.
  const filterRangeKey = useMemo(() => {
    if (!timezone || matchReadyTz !== timezone) return null;
    const { start, end } = storeApiBoundsFromMatch(timezone, match.start, match.end);
    return `${start}|${end}|${timezone}`;
  }, [timezone, matchReadyTz, match]);

  useEffect(() => {
    if (!filterRangeKey) return;
    let cancelled = false;
    const [start, end, tz] = filterRangeKey.split('|');
    (async () => {
      try {
        const token = await getJwtToken();
        const res = await axios.post(
          `${API}/filter-candidates`,
          { start, end, timezone: tz },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (cancelled) return;
        if (!res.data?.connected) {
          setConnected(false);
          return;
        }
        setConnected(true);
        setCandidates(res.data);
        setCandidatesRangeKey(filterRangeKey);
        setFilters({
          campaign_ids: (res.data.campaigns || []).map((c) => c.id),
          adset_ids: (res.data.adsets || []).map((c) => c.id),
          ad_ids: (res.data.ads || []).map((c) => c.id),
          effective_statuses: [...(res.data.effective_statuses || [])],
          objectives: [...(res.data.objectives || [])]
        });
        setTablePage(1);
      } catch (err) {
        console.log(err);
        if (!cancelled) setError('Failed to load Meta Ads filters.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [filterRangeKey, getJwtToken]);

  // Ad set / ad options cascade from the campaign / ad set selection.
  const adsetOptions = useMemo(() => {
    if (!candidates) return [];
    const campaigns = new Set(filters.campaign_ids);
    return candidates.adsets.filter((a) => campaigns.has(a.campaign_id));
  }, [candidates, filters.campaign_ids]);
  const adOptions = useMemo(() => {
    if (!candidates) return [];
    const adsets = new Set(filters.adset_ids);
    return candidates.ads.filter((a) => adsets.has(a.adset_id));
  }, [candidates, filters.adset_ids]);

  const patchFilters = (patch) => {
    setTablePage(1);
    setFilters((prev) => {
      const next = { ...prev, ...patch };
      if (!candidates) return next;
      if (patch.campaign_ids) {
        const campaigns = new Set(next.campaign_ids);
        next.adset_ids = candidates.adsets.filter((a) => campaigns.has(a.campaign_id)).map((a) => a.id);
      }
      if (patch.campaign_ids || patch.adset_ids) {
        const adsets = new Set(next.adset_ids);
        next.ad_ids = candidates.ads.filter((a) => adsets.has(a.adset_id)).map((a) => a.id);
      }
      return next;
    });
  };

  const request = useMemo(() => {
    if (!timezone || matchReadyTz !== timezone || !candidates || candidatesRangeKey !== filterRangeKey) {
      return { key: null, params: null };
    }
    const { start, end } = storeApiBoundsFromMatch(timezone, match.start, match.end);
    const campaignUniverse = candidates.campaigns.map((c) => c.id);
    const params = {
      start,
      end,
      timezone,
      campaign_ids: narrowed(filters.campaign_ids, campaignUniverse),
      adset_ids: narrowed(filters.adset_ids, adsetOptions.map((a) => a.id)),
      ad_ids: narrowed(filters.ad_ids, adOptions.map((a) => a.id)),
      effective_statuses: narrowed(filters.effective_statuses, candidates.effective_statuses),
      objectives: narrowed(filters.objectives, candidates.objectives)
    };
    return { key: JSON.stringify(params), params };
  }, [
    timezone,
    matchReadyTz,
    candidates,
    candidatesRangeKey,
    filterRangeKey,
    match,
    filters,
    adsetOptions,
    adOptions
  ]);

  useEffect(() => {
    const { key, params } = request;
    if (!key) return;
    let cancelled = false;
    (async () => {
      setLoadingAnalytics(true);
      setError('');
      try {
        const token = await getJwtToken();
        const res = await axios.post(
          `${API}/analytics`,
          { ...params, time_grain: timeGrain },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (cancelled) return;
        if (!res.data?.connected) {
          setConnected(false);
          return;
        }
        setAnalytics(res.data);
      } catch (err) {
        if (cancelled) return;
        console.log(err);
        setError('Failed to load Meta Ads analytics.');
        setAnalytics(null);
      } finally {
        if (!cancelled) setLoadingAnalytics(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [request.key, timeGrain, getJwtToken]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const { key, params } = request;
    if (!key) return;
    let cancelled = false;
    (async () => {
      setLoadingTable(true);
      try {
        const token = await getJwtToken();
        const res = await axios.post(
          `${API}/table`,
          { ...params, level: 'campaign', page: tablePage, pageSize: TABLE_PAGE_SIZE, ...tableSort },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!cancelled) setTable(res.data);
      } catch (err) {
        console.log(err);
        if (!cancelled) setTable(null);
      } finally {
        if (!cancelled) setLoadingTable(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [request.key, tablePage, tableSort.sort_by, tableSort.sort_dir, getJwtToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // Child rows belong to one request + sort; reset them whenever either changes.
  const childrenKey = `${request.key}|${tableSort.sort_by}|${tableSort.sort_dir}`;
  useEffect(() => {
    childrenKeyRef.current = childrenKey;
    setChildren({});
  }, [childrenKey]);

  useEffect(() => {
    const { params } = request;
    if (!params) return;
    const missing = Object.keys(expanded).filter((k) => expanded[k] && !children[k]);
    if (!missing.length) return;
    const key = childrenKey;
    setChildren((prev) => {
      const next = { ...prev };
      for (const k of missing) next[k] = { status: 'loading', rows: [] };
      return next;
    });
    missing.forEach(async (k) => {
      const [parentLevel, id] = k.split(':');
      try {
        const token = await getJwtToken();
        const res = await axios.post(
          `${API}/table`,
          {
            ...params,
            level: parentLevel === 'campaign' ? 'adset' : 'ad',
            ...(parentLevel === 'campaign' ? { parent_campaign_id: id } : { parent_adset_id: id }),
            page: 1,
            pageSize: CHILD_PAGE_SIZE,
            ...tableSort
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (childrenKeyRef.current !== key) return;
        setChildren((prev) => ({ ...prev, [k]: { status: 'ready', rows: res.data?.rows || [] } }));
      } catch (err) {
        console.log(err);
        if (childrenKeyRef.current !== key) return;
        setChildren((prev) => ({ ...prev, [k]: { status: 'error', rows: [] } }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, children, childrenKey, getJwtToken]);

  const toggleExpanded = (k) => {
    setChildren((prev) => {
      if (prev[k]?.status !== 'error') return prev;
      const { [k]: _failed, ...rest } = prev;
      return rest;
    });
    setExpanded((prev) => ({ ...prev, [k]: !prev[k] }));
  };

  const sortBy = (id) =>
    setTableSort((s) => ({ sort_by: id, sort_dir: s.sort_by === id ? s.sort_dir * -1 : -1 }));

  const changes = useMemo(() => {
    const list = analytics?.changes || [];
    return changeLevel === 'all' ? list : list.filter((c) => c.object_level === changeLevel);
  }, [analytics, changeLevel]);

  const sync = analytics?.sync;
  const syncRunning = sync && ['queued', 'backfilling', 'running'].includes(sync.status);
  const syncProgressLabel = syncRunning
    ? `Backfilling ${
        sync.chunks_total
          ? `${Math.min(100, Math.round(((sync.chunks_processed || 0) / sync.chunks_total) * 100))}%`
          : '…'
      }`
    : null;

  // Poll sync progress while a backfill runs so the header % updates without a full refresh.
  useEffect(() => {
    if (!syncRunning) return;
    const timer = setInterval(async () => {
      try {
        const token = await getJwtToken();
        const res = await axios.get('https://api.amplifyloyalty.com/app/stores/integrations', {
          headers: { Authorization: `Bearer ${token}` },
          params: { integration: 'meta-ads' }
        });
        const nextSync = res.data?.sync;
        if (!nextSync) return;
        setAnalytics((prev) => (prev ? { ...prev, sync: nextSync } : prev));
      } catch (err) {
        console.log(err);
      }
    }, 10000);
    return () => clearInterval(timer);
  }, [syncRunning, getJwtToken]);

  const formatChangeValue = (v) => {
    if (v == null || v === '') return '—';
    if (typeof v === 'object') {
      if ('daily_budget' in v || 'lifetime_budget' in v) {
        if (v.daily_budget != null) return `${moneyFormat.format(v.daily_budget)}/day`;
        if (v.lifetime_budget != null) return `${moneyFormat.format(v.lifetime_budget)} lifetime`;
        return '—';
      }
      if (v.id && ('body' in v || 'title' in v)) return v.title || v.body?.slice(0, 40) || `Creative ${v.id}`;
      const s = JSON.stringify(v);
      return s.length > 60 ? `${s.slice(0, 57)}…` : s;
    }
    const s = /^[A-Z_]+$/.test(String(v)) ? humanize(v) : String(v);
    return s.length > 60 ? `${s.slice(0, 57)}…` : s;
  };

  const leftPriorKey = leftMetric ? `${leftMetric}Prior` : null;
  const rightPriorKey = rightMetric ? `${rightMetric}Prior` : null;

  // Align prior series onto the current x-axis by index so both periods share the chart domain.
  const chartData = useMemo(() => {
    const curr = Array.isArray(analytics?.series) ? analytics.series : [];
    if ((!showLeftPrior || !leftMetric) && (!showRightPrior || !rightMetric)) return curr;
    const prior = Array.isArray(analytics?.prior_series) ? analytics.prior_series : [];
    return curr.map((point, i) => {
      const p = prior[i] || {};
      const next = { ...point };
      if (showLeftPrior && leftMetric) next[leftPriorKey] = p[leftMetric] ?? 0;
      if (showRightPrior && rightMetric) next[rightPriorKey] = p[rightMetric] ?? 0;
      return next;
    });
  }, [
    analytics?.series,
    analytics?.prior_series,
    showLeftPrior,
    showRightPrior,
    leftMetric,
    rightMetric,
    leftPriorKey,
    rightPriorKey
  ]);

  if (connected === false) return <Navigate to="/app/product-analysis/analytics" replace />;

  const summary = analytics?.summary;
  const summaryDelta = analytics?.summary_delta;
  const filtersDisabled =
    !candidates || candidatesRangeKey !== filterRangeKey || loadingAnalytics || loadingTable;
  const rangeDays = analytics?.range?.day_count
    || (() => {
      const from = analytics?.range?.from;
      const to = analytics?.range?.to;
      if (!from || !to) return 0;
      return Math.max(
        1,
        Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86400000) + 1
      );
    })();
  const avgDailySpend = rangeDays > 0 ? (summary?.spend || 0) / rangeDays : 0;
  const leftOptions = [
    ...SERIES_METRIC_OPTIONS.filter((o) => o.id !== rightMetric),
    SERIES_NONE
  ];
  const rightOptions = [
    ...SERIES_METRIC_OPTIONS.filter((o) => o.id !== leftMetric),
    SERIES_NONE
  ];

  const setLeftSeries = (id) => {
    setLeftMetric(id);
    if (!id) setShowLeftPrior(false);
  };
  const setRightSeries = (id) => {
    setRightMetric(id);
    if (!id) setShowRightPrior(false);
  };

  const renderComparisonToggle = (side) => {
    const checked = side === 'left' ? showLeftPrior : showRightPrior;
    const setChecked = side === 'left' ? setShowLeftPrior : setShowRightPrior;
    return (
      <label
        className="meta-ads-comparison-toggle"
        title={
          analytics?.prior_range
            ? `Compare ${analytics.prior_range.from} – ${analytics.prior_range.to}`
            : 'Compare prior period'
        }
      >
        <span className="meta-ads-comparison-toggle__label">Comparison</span>
        <span className="switch">
          <input
            className="switch-input"
            type="checkbox"
            checked={checked}
            disabled={loadingAnalytics}
            onChange={(e) => setChecked(e.target.checked)}
          />
          <span className="slider" />
        </span>
      </label>
    );
  };

  const scorecard = (title, kind, value, deltaKey, { invertChange = false } = {}) => (
    <Scorecard
      className="dashboard__scorecard"
      title={title}
      data={loadingAnalytics || !summary ? '—' : fmt(kind, value)}
      change={loadingAnalytics || !summary ? undefined : summaryDelta?.[deltaKey] ?? null}
      invertChange={invertChange}
    />
  );

  const renderMetricCells = (row) =>
    TABLE_COLUMNS.map((col) => (
      <div key={col.id} className="meta-ads-table__cell meta-ads-table__cell--metric">
        {fmt(col.format, row[col.id])}
      </div>
    ));

  const formatBudget = (row) => {
    if (row?.daily_budget != null) return `${moneyFormat.format(row.daily_budget)}/day`;
    if (row?.lifetime_budget != null) return `${moneyFormat.format(row.lifetime_budget)} life`;
    return '—';
  };

  const renderBudgetCell = (row) => (
    <div className="meta-ads-table__cell meta-ads-table__cell--budget">
      {row ? formatBudget(row) : ''}
    </div>
  );

  const renderStatusDot = (row) => {
    const s = String(row.effective_status || row.status || '').toUpperCase();
    if (!s) return <span className="meta-ads-table__status-dot" aria-hidden="true" />;
    const active = s === 'ACTIVE';
    return (
      <span
        className={`meta-ads-table__status-dot${active ? ' meta-ads-table__status-dot--active' : ''}`}
        title={humanize(s)}
        aria-label={humanize(s)}
      />
    );
  };

  const renderChildRows = (k, depth) => {
    const state = children[k];
    if (!state || state.status === 'loading') {
      return (
        <div className={`meta-ads-table__row meta-ads-table__row--depth-${depth} meta-ads-table__row--muted`}>
          <div className="meta-ads-table__cell meta-ads-table__cell--name">Loading…</div>
          {renderBudgetCell(null)}
        </div>
      );
    }
    if (state.status === 'error') {
      return (
        <div className={`meta-ads-table__row meta-ads-table__row--depth-${depth} meta-ads-table__row--muted`}>
          <div className="meta-ads-table__cell meta-ads-table__cell--name">Failed to load. Collapse and expand to retry.</div>
          {renderBudgetCell(null)}
        </div>
      );
    }
    if (!state.rows.length) {
      return (
        <div className={`meta-ads-table__row meta-ads-table__row--depth-${depth} meta-ads-table__row--muted`}>
          <div className="meta-ads-table__cell meta-ads-table__cell--name">No delivery in range</div>
          {renderBudgetCell(null)}
        </div>
      );
    }
    return state.rows.map((row) => {
      if (depth === 1) {
        const childKey = `adset:${row.id}`;
        return (
          <div key={row.id}>
            <div className="meta-ads-table__row meta-ads-table__row--depth-1">
              <div
                className="meta-ads-table__cell meta-ads-table__cell--name meta-ads-table__cell--name-expandable"
                role="button"
                tabIndex={0}
                aria-expanded={!!expanded[childKey]}
                onClick={() => toggleExpanded(childKey)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleExpanded(childKey);
                  }
                }}
              >
                <span className="meta-ads-table__expander" aria-hidden="true">
                  {expanded[childKey] ? '▾' : '▸'}
                </span>
                {renderStatusDot(row)}
                <span className="meta-ads-table__name" title={row.name}>{row.name}</span>
              </div>
              {renderBudgetCell(row)}
              {renderMetricCells(row)}
            </div>
            {expanded[childKey] && renderChildRows(childKey, 2)}
          </div>
        );
      }
      const creative = row.creative;
      return (
        <div key={row.id} className="meta-ads-table__row meta-ads-table__row--depth-2">
          <div className="meta-ads-table__cell meta-ads-table__cell--name">
            {renderStatusDot(row)}
            {creative?.thumbnail_url || creative?.image_url ? (
              <img
                className="meta-ads-table__thumb"
                src={creative.thumbnail_url || creative.image_url}
                alt=""
              />
            ) : (
              <span className="meta-ads-table__thumb meta-ads-table__thumb--empty" />
            )}
            <span className="meta-ads-table__name" title={creative?.body || row.name}>{row.name}</span>
          </div>
          {renderBudgetCell(row)}
          {renderMetricCells(row)}
        </div>
      );
    });
  };

  return (
    <div className="product-analysis-scroll">
      <div className="dashboard dashboard--product-analysis">
        <div className="dashboard__filters product-analysis-filters product-analysis-filters--split-rows">
          <div className="product-analysis-filters__stack">
            <div className="product-analysis-filters__left">
              <div className="meta-ads-header-title">
                <AnalyticsSourceSelector current="meta-ads" activeRange={activeRange} match={match} timezone={timezone} />
                {syncProgressLabel && (
                  <span className="meta-ads-sync-progress">{syncProgressLabel}</span>
                )}
              </div>
              <div className="date-filter promotions-table-filters__filter-wrapper">
                <StoreDateRangeFilter
                  active={activeRange}
                  setActive={setActiveRange}
                  timezone={timezone}
                  match={match}
                  disabled={filtersDisabled}
                  popoverAlign="start"
                  options={[
                    ...RANGE_PRESETS.map((preset) => ({
                      text: preset.label,
                      action: () => setMatch(preset.resolve(timezone))
                    })),
                    {
                      text: CUSTOM_RANGE_LABEL,
                      action: (customStart, customEnd) => setMatch({ start: customStart, end: customEnd })
                    }
                  ]}
                />
              </div>
            </div>
            <div className="product-analysis-filters__filter-panel">
              <IdNameMultiSelectFilter
                label="Campaigns"
                options={candidates?.campaigns || []}
                value={filters.campaign_ids}
                onChange={(campaign_ids) => patchFilters({ campaign_ids })}
                showSearch
                searchPlaceholder="Search campaigns"
                emptyText="No campaigns"
                disabled={filtersDisabled}
              />
              <IdNameMultiSelectFilter
                label="Ad sets"
                options={adsetOptions}
                value={filters.adset_ids}
                onChange={(adset_ids) => patchFilters({ adset_ids })}
                showSearch
                searchPlaceholder="Search ad sets"
                emptyText="No ad sets"
                disabled={filtersDisabled}
              />
              <IdNameMultiSelectFilter
                label="Ads"
                options={adOptions}
                value={filters.ad_ids}
                onChange={(ad_ids) => patchFilters({ ad_ids })}
                showSearch
                searchPlaceholder="Search ads"
                emptyText="No ads"
                disabled={filtersDisabled}
              />
              <MultiSelectFilter
                label="Delivery"
                options={candidates?.effective_statuses || []}
                value={filters.effective_statuses}
                onChange={(effective_statuses) => patchFilters({ effective_statuses })}
                disabled={filtersDisabled}
              />
              <MultiSelectFilter
                label="Objective"
                options={candidates?.objectives || []}
                value={filters.objectives}
                onChange={(objectives) => patchFilters({ objectives })}
                disabled={filtersDisabled}
              />
            </div>
          </div>
        </div>

        {error && <div className="float-element" style={{ padding: 12 }}>{error}</div>}

        {sync?.status === 'error' && (
          <div className="float-element meta-ads-banner meta-ads-banner--error">
            The last Meta Ads sync failed. Check the <Link to="/app/integrations/meta-ads">integration</Link>.
          </div>
        )}

        <div
          className="dashboard__row"
          style={{ position: 'relative', display: 'flex', alignItems: 'stretch', gap: 16, width: '100%', flexWrap: 'nowrap' }}
        >
          <div
            className="float-element product-analysis-scorecards-wrap"
            style={{ width: '33%', maxWidth: '415px', flexShrink: 0, boxSizing: 'border-box', padding: '20px' }}
          >
            <div className="product-analysis-scorecards meta-ads-scorecards">
              {scorecard('Spend', 'money', summary?.spend, 'spend')}
              {scorecard('Avg. Daily Spend', 'money', avgDailySpend, 'avg_daily_spend')}
              {scorecard('ROAS', 'ratio', summary?.roas, 'roas')}
              {scorecard('CPA', 'money', summary?.cpa, 'cpa', { invertChange: true })}
              {scorecard('CTR', 'percent', summary?.ctr, 'ctr')}
              {scorecard('CPC', 'money', summary?.cpc, 'cpc', { invertChange: true })}
              {scorecard('Purchases', 'decimal', summary?.purchases, 'purchases')}
              {scorecard('Purchase Value', 'money', summary?.purchase_value, 'purchase_value')}
            </div>
          </div>
          <div style={{ flex: '1 1 0', minWidth: 0, minHeight: 0, position: 'relative', display: 'flex', alignSelf: 'stretch' }}>
            <LineGraph
              width="100%"
              height="100%"
              data={chartData}
              heading={
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', width: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minWidth: 0 }}>
                    <span>Performance over Time</span>
                    <SingleSelectDropdown
                      ariaLabel="Left series"
                      options={leftOptions}
                      value={leftMetric}
                      onChange={setLeftSeries}
                      disabled={loadingAnalytics}
                      triggerClassName="product-analysis-filters__multiselect-trigger--series-left"
                      firstOptionAccessory={leftMetric ? renderComparisonToggle('left') : null}
                    />
                    <span aria-hidden="true">x</span>
                    <SingleSelectDropdown
                      ariaLabel="Right series"
                      options={rightOptions}
                      value={rightMetric}
                      onChange={setRightSeries}
                      disabled={loadingAnalytics}
                      triggerClassName="product-analysis-filters__multiselect-trigger--series-right"
                      firstOptionAccessory={rightMetric ? renderComparisonToggle('right') : null}
                    />
                  </div>
                  <SingleSelectDropdown
                    label="Time"
                    ariaLabel="Time dimension"
                    options={TIME_GRAIN_OPTIONS}
                    value={timeGrain}
                    onChange={setTimeGrain}
                    disabled={loadingAnalytics}
                    triggerClassName="product-analysis-filters__multiselect-trigger--time"
                  />
                </div>
              }
              className="float-element product-analysis-line-graph"
              showLegend={false}
              keys={[leftMetric, rightMetric]}
              comparisonKeys={[
                showLeftPrior && leftMetric ? leftPriorKey : null,
                showRightPrior && rightMetric ? rightPriorKey : null
              ]}
              keyLabels={{
                ...(leftMetric
                  ? {
                      [leftMetric]: SERIES_METRICS[leftMetric].label,
                      [leftPriorKey]: `${SERIES_METRICS[leftMetric].label} (prior)`
                    }
                  : {}),
                ...(rightMetric
                  ? {
                      [rightMetric]: SERIES_METRICS[rightMetric].label,
                      [rightPriorKey]: `${SERIES_METRICS[rightMetric].label} (prior)`
                    }
                  : {})
              }}
              formatter={(value, name) => {
                if (leftMetric) {
                  const leftLabel = SERIES_METRICS[leftMetric].label;
                  if (name === leftLabel || name === `${leftLabel} (prior)`) {
                    return fmt(SERIES_METRICS[leftMetric].format, value);
                  }
                }
                if (rightMetric) {
                  const rightLabel = SERIES_METRICS[rightMetric].label;
                  if (name === rightLabel || name === `${rightLabel} (prior)`) {
                    return fmt(SERIES_METRICS[rightMetric].format, value);
                  }
                }
                return value;
              }}
              yLeftTickFormatter={
                leftMetric ? (v) => fmtTick(SERIES_METRICS[leftMetric].format, v) : undefined
              }
              yRightTickFormatter={
                rightMetric ? (v) => fmtTick(SERIES_METRICS[rightMetric].format, v) : undefined
              }
              tickFormatter={(v) => formatGrainTick(v, timeGrain)}
              labelFormatter={(v) => formatGrainLabel(v, timeGrain)}
            />
          </div>
        </div>

        <section className="product-analysis-table__container" style={{ position: 'relative', width: '100%', marginTop: 20, boxSizing: 'border-box' }}>
          <div className="product-analysis-table meta-ads-table">
            <div className="meta-ads-table__scroll">
              <div className="meta-ads-table__row meta-ads-table__row--header">
                <div className="meta-ads-table__cell meta-ads-table__cell--name">Campaign / Ad set / Ad</div>
                <div className="meta-ads-table__cell meta-ads-table__cell--budget">Budget</div>
                {TABLE_COLUMNS.map((col) => (
                  <div
                    key={col.id}
                    className={`meta-ads-table__cell meta-ads-table__cell--metric meta-ads-table__sort${tableSort.sort_by === col.id ? ' meta-ads-table__sort--active' : ''}`}
                    onClick={() => sortBy(col.id)}
                  >
                    {col.label}
                    {tableSort.sort_by === col.id && (
                      <Caret
                        className="meta-ads-table__heading-caret"
                        direction={tableSort.sort_dir}
                      />
                    )}
                  </div>
                ))}
              </div>
              {loadingTable && !table ? (
                <div className="meta-ads-table__loading">
                  <img src={loadingBuffering} alt="Loading" width={32} />
                </div>
              ) : !table?.rows?.length ? (
                <div className="meta-ads-table__row meta-ads-table__row--muted">
                  <div className="meta-ads-table__cell meta-ads-table__cell--name">No delivery in this range.</div>
                  {renderBudgetCell(null)}
                </div>
              ) : (
                <>
                  {table.rows.map((row) => {
                    const k = `campaign:${row.id}`;
                    return (
                      <div key={row.id}>
                        <div className="meta-ads-table__row">
                          <div
                            className="meta-ads-table__cell meta-ads-table__cell--name meta-ads-table__cell--name-expandable"
                            role="button"
                            tabIndex={0}
                            aria-expanded={!!expanded[k]}
                            onClick={() => toggleExpanded(k)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                toggleExpanded(k);
                              }
                            }}
                          >
                            <span className="meta-ads-table__expander" aria-hidden="true">
                              {expanded[k] ? '▾' : '▸'}
                            </span>
                            {renderStatusDot(row)}
                            <span className="meta-ads-table__name" title={row.name}>{row.name}</span>
                          </div>
                          {renderBudgetCell(row)}
                          {renderMetricCells(row)}
                        </div>
                        {expanded[k] && renderChildRows(k, 1)}
                      </div>
                    );
                  })}
                  <div className="meta-ads-table__row meta-ads-table__row--totals">
                    <div className="meta-ads-table__cell meta-ads-table__cell--name">Total</div>
                    {renderBudgetCell(null)}
                    {renderMetricCells(table.totals || {})}
                  </div>
                </>
              )}
            </div>
            {table?.page_info && table.page_info.total > TABLE_PAGE_SIZE && (
              <TablePagination
                options={{
                  page: table.page_info.page,
                  limit: table.page_info.page_size,
                  totalDocs: table.page_info.total,
                  hasNextPage: table.page_info.page * table.page_info.page_size < table.page_info.total,
                  hasPrevPage: table.page_info.page > 1
                }}
                docType="campaigns"
                previous={() => setTablePage((p) => Math.max(1, p - 1))}
                next={() => setTablePage((p) => p + 1)}
              />
            )}
          </div>
        </section>

        <section className="float-element meta-ads-changes">
          <div className="meta-ads-changes__header">
            <div className="line-graph__heading">Change Log</div>
            <SingleSelectDropdown
              ariaLabel="Change level"
              options={CHANGE_LEVEL_OPTIONS}
              value={changeLevel}
              onChange={setChangeLevel}
            />
          </div>
          {!changes.length ? (
            <div className="meta-ads-changes__empty">No changes recorded in this range.</div>
          ) : (
            <div className="meta-ads-changes__table">
              <div className="meta-ads-changes__item meta-ads-changes__item--header" role="row">
                <span className="meta-ads-changes__dot" aria-hidden="true" />
                <span className="meta-ads-changes__time">Time</span>
                <span className="meta-ads-changes__type">Type</span>
                <span className="meta-ads-changes__level">Level</span>
                <span className="meta-ads-changes__object">Name</span>
                <span className="meta-ads-changes__detail">Change</span>
                <span className="meta-ads-changes__actor">Actor</span>
              </div>
              <ul className="meta-ads-changes__list">
                {changes.map((c, i) => (
                  <li key={`${c.object_id}-${c.event_time}-${i}`} className="meta-ads-changes__item">
                    <span className="meta-ads-changes__dot" style={{ background: CHANGE_TYPE_COLORS[c.event_type] || CHANGE_TYPE_COLORS.other }} />
                    <span className="meta-ads-changes__time">
                      {new Date(c.event_time).toLocaleString('en-US', {
                        timeZone: timezone || undefined,
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit'
                      })}
                    </span>
                    <span className="meta-ads-changes__type">{CHANGE_TYPE_LABELS[c.event_type] || humanize(c.event_type)}</span>
                    <span className="meta-ads-changes__level">{LEVEL_LABELS[c.object_level] || c.object_level}</span>
                    <span className="meta-ads-changes__object" title={c.object_name}>{c.object_name || c.object_id}</span>
                    <span className="meta-ads-changes__detail">
                      {c.event_type === 'created'
                        ? c.label || 'Launched'
                        : c.before != null || c.after != null
                          ? `${formatChangeValue(c.before)} → ${formatChangeValue(c.after)}`
                          : c.label || ''}
                    </span>
                    <span className="meta-ads-changes__actor">{c.actor_name || '—'}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
